import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { getGameRNG } from '../core/rng';
import { computeSafeSpawnPosition } from './spawnHelper';

const BANANA_PHYSICS = {
  BOUNCE_HORIZONTAL: 0.98,
  BOUNCE_VERTICAL: 0.85,
  FRICTION: 0.99,
  EXPLOSION_RADIUS: 55,
  MAX_DAMAGE: 40,
  CLUSTER_COUNT: 5,
  CLUSTER_RADIUS: 40,
  CLUSTER_DAMAGE: 35,
  CLUSTER_BOUNCE_H: 0.95,
  CLUSTER_BOUNCE_V: 0.8,
};

export const WeaponBananaBomb: WeaponDefinition = {
  id: 'banana',
  name: 'Banana Bomb',
  icon: 'banana.1.png',
  category: 'thrown',
  maxAmmo: 3,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: true,
  timerRange: [1, 5],
  showAimGuide: true,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, timer, world } = ctx;

    const maxSpeed = 750;
    const speed = (power / 100) * maxSpeed;
    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

    const spawnDistance = 20;
    const spawn = computeSafeSpawnPosition(worm, aimRad, spawnDistance, world);

    const banana = new Projectile({
      x: spawn.x,
      y: spawn.y,
      vx: Math.cos(aimRad) * speed,
      vy: -Math.sin(aimRad) * speed,
      type: 'banana',
      timer: timer,
      bounceH: BANANA_PHYSICS.BOUNCE_HORIZONTAL,
      bounceV: BANANA_PHYSICS.BOUNCE_VERTICAL,
      friction: BANANA_PHYSICS.FRICTION,
      windAffected: false,
      explosionRadius: BANANA_PHYSICS.EXPLOSION_RADIUS,
      maxDamage: BANANA_PHYSICS.MAX_DAMAGE,
      ownerId: worm.id,
    });

    banana.onExplode = (x, y) => {
      spawnBananalets(x, y, worm.id, world, world.getNetworkManager?.());
    };

    return {
      success: true,
      entities: [banana],
    };
  },
};

function spawnBananalets(
  x: number,
  y: number,
  ownerId: string,
  world: WeaponContext['world'],
  networkManager?: any,
): void {
  const count = BANANA_PHYSICS.CLUSTER_COUNT;
  const rng = getGameRNG();
  const clusters: any[] = [];

  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count - Math.PI / 2;
    const speed = rng.nextFloat(200, 350);

    const bananalet = new Projectile({
      x: x,
      y: y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 150,
      type: 'bananalet',
      timer: rng.nextFloat(1.5, 3),
      bounceH: BANANA_PHYSICS.CLUSTER_BOUNCE_H,
      bounceV: BANANA_PHYSICS.CLUSTER_BOUNCE_V,
      friction: BANANA_PHYSICS.FRICTION,
      windAffected: false,
      explosionRadius: BANANA_PHYSICS.CLUSTER_RADIUS,
      maxDamage: BANANA_PHYSICS.CLUSTER_DAMAGE,
      ownerId: ownerId,
    });

    world.addEntity(bananalet);

    clusters.push({
      id: bananalet.id,
      type: bananalet.type,
      position: { x: bananalet.position.x, y: bananalet.position.y },
      velocity: { x: bananalet.velocity.x, y: bananalet.velocity.y },
      timer: bananalet.timer,
      rotation: bananalet.rotation,
      isOnGround: bananalet.isOnGround,
      explosionRadius: bananalet.explosionRadius,
      maxDamage: bananalet.maxDamage,
      ownerId: bananalet.ownerId,
    });
  }

  if (networkManager) {
    networkManager.broadcastGameEvent({
      type: 'clusterSpawn',
      projectiles: clusters,
    });
  }
}
